import { useEffect } from 'react';

/**
 * Keeps the screen on while `active` (Screen Wake Lock API: Safari 16.4+, Chrome, Firefox 126+;
 * a no-op elsewhere). The browser drops the lock when the page is hidden; it is asked for again
 * when the page is visible and still active. Release it as soon as practice stops, so the phone
 * can sleep when nobody is practising.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;
    const api = navigator.wakeLock;
    let sentinel: WakeLockSentinel | null = null;
    const state = { cancelled: false };
    const acquire = () => {
      if (state.cancelled || document.hidden || sentinel) return;
      api
        .request('screen')
        .then((s) => {
          if (state.cancelled) {
            void s.release();
            return;
          }
          sentinel = s;
          s.addEventListener('release', () => {
            sentinel = null;
          });
        })
        .catch(() => {
          // Not allowed now (no user gesture yet, low battery mode): the screen may sleep.
        });
    };
    const onVisibility = () => {
      if (!document.hidden) acquire();
    };
    acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      state.cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void sentinel?.release();
      sentinel = null;
    };
  }, [active]);
}
